import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Radio, RadioGroup, Switch, Button, Checkbox, FormGroup } from '@mui/material';
import { useController, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import PageListManualBlockItem from '@/editor/PageEditor/EditorBlock/PageListBlocks/PageListManualBlockItem';
import { noop } from '@/utils/functions';
import { useStore } from '../../../../components/store/Store';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import Container from '../../../../components/Container';
import M24PageFinder from '../../../../components/Finder/M24PageFinder/M24PageFinder';
import Styles from '../../../../assets/js/Styles';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { PageListAutoBlock as PageListAutoBlockModel } from '../../../../declarations/models/blocks/PageListBlocks';
import PageAutoListingSelects from './PageAutoListingSelects';
import PageListCategorySettings from './PageListCategorySettings';
import PageListPageTreeSettings from './PageListPageTreeSettings';
import PageListDisplayOptions from './PageListDisplayOptions';
import SiteSharingBlockSettings from '../SiteSharingBlockSettings';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

export const settingsAreaStyle = {
  width: '100%',
  backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
  padding: 2,
  borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
  gap: 2,
};

export const PageListAutoBlock: BlockComponent = () => {
  const { blockPath } = useCurrentBlock();
  const { t } = useTranslation('components');
  const { state } = useStore();

  const [pageFinderOpen, setPageFinderOpen] = useState<boolean>(false);
  const [linkPageFinderOpen, setLinkPageFinderOpen] = useState<boolean>(false);
  const params = useParams<{ modelId?: string }>();

  const currentPageId = params.modelId ? parseInt(params.modelId, 10) : undefined;

  const currentViewType = useWatch<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.view`>({
    name: `${blockPath}.view`,
  });

  const currentLayout = useWatch<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.layout`>({
    name: `${blockPath}.layout`,
  });

  const {
    field: { value: searchPathPageId, onChange: onChangeSearchPath },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.searchPathPageId`>({
    name: `${blockPath}.searchPathPageId`,
    defaultValue: params.modelId ? parseInt(params.modelId, 10) : undefined,
  });

  const {
    field: { value: searchLevel, onChange: onChangeSearchLevel },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.searchLevel`>({
    name: `${blockPath}.searchLevel`,
    defaultValue: 'children',
  });

  const {
    field: { value: displayType, onChange: onChangeDisplayType },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.displayType`>({
    name: `${blockPath}.displayType`,
  });

  const {
    field: { value: siteIds, onChange: setSiteIds },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.siteIds`>({
    name: `${blockPath}.siteIds`,
  });

  const {
    field: { value: userMuseumFilter, onChange: setUserMuseumFilter },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.userMuseumFilter`>({
    name: `${blockPath}.userMuseumFilter`,
  });

  const {
    field: { value: userOrderFilter, onChange: setUserOrderFilter },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.userOrderFilter`>({
    name: `${blockPath}.userOrderFilter`,
  });

  const {
    field: { value: enableSearch, onChange: setEnableSearch },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.enableSearch`>({
    name: `${blockPath}.enableSearch`,
  });

  const {
    field: { value: showAllLinkPage, onChange: onChangeShowAllLinkPage },
  } = useController<BlockSpecificPage<PageListAutoBlockModel>, `${BlockPath}.showAllLinkPage`>({
    name: `${blockPath}.showAllLinkPage`,
  });

  const [userFiltering, setUserFiltering] = useState<boolean>(
    !!userMuseumFilter || !!userOrderFilter || !!enableSearch,
  );

  const handleChangeUserFiltering = () => {
    if (userFiltering) {
      setUserMuseumFilter(false);
      setEnableSearch(false);
      setUserOrderFilter(false);
    }
    setUserFiltering(!userFiltering);
  };

  return (
    <Container fullWidth column mt={1} gap={2} left>
      <M24PageFinder
        hideSiteSelector
        open={pageFinderOpen}
        onClose={() => setPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeSearchPath(pages[0].source.id);
          }
        }}
      />

      <M24PageFinder
        hideSiteSelector
        open={linkPageFinderOpen}
        onClose={() => setLinkPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeShowAllLinkPage(pages[0].source);
          }
        }}
      />

      <Container>
        <RadioGroup value={displayType} onChange={(_, v) => onChangeDisplayType(v)} row>
          <FormControlLabel control={<Radio value='pageTree' />} label={t('PageListAutoBlock.PageTreeButtonLabel')} />
          <FormControlLabel
            control={<Radio value='categories' />}
            label={t('PageListAutoBlock.SelectCategoryButtonLabel')}
          />
        </RadioGroup>
      </Container>
      <Container
        column
        fullWidth
        sx={{
          backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
        }}
        p={2}
        gap={2}>
        <Container fullWidth left column gap={2}>
          {displayType === 'pageTree' ? (
            <PageListPageTreeSettings
              searchPathPageId={searchPathPageId}
              setSearchPathPageId={onChangeSearchPath}
              currentPageId={currentPageId}
              startPageId={state.selectedSiteDefaultPageId || undefined}
              searchLevel={searchLevel}
              setSearchLevel={onChangeSearchLevel}
              openPageFinder={() => setPageFinderOpen(true)}
            />
          ) : (
            <>
              <PageListCategorySettings blockPath={blockPath} />
              <SiteSharingBlockSettings siteIds={siteIds} setSiteIds={setSiteIds} />
            </>
          )}
          <PageAutoListingSelects blockPath={blockPath} />
          <PageListDisplayOptions blockPath={blockPath} viewType={currentViewType} layout={currentLayout} />
        </Container>
      </Container>

      <OutlinedContainer fullWidth column left label={t('PageListManualBlock.FilteringOptions')}>
        <FormControlLabel
          control={<Switch checked={userFiltering} onChange={handleChangeUserFiltering} color='secondary' />}
          label={t('EventsBlock.UserFilteringLabel')}
        />
        <FormGroup row>
          <FormControlLabel
            control={<Checkbox checked={!!userMuseumFilter} onChange={(_, v) => setUserMuseumFilter(v)} />}
            disabled={!userFiltering}
            label={t('EventsBlock.UseMuseumFiltering')}
          />
          <FormControlLabel
            control={<Checkbox checked={!!userOrderFilter} onChange={(_, v) => setUserOrderFilter(v)} />}
            disabled={!userFiltering}
            label={t('EventsBlock.OrderFilteringLabel')}
          />
          <FormControlLabel
            control={<Checkbox checked={!!enableSearch} onChange={(_, v) => setEnableSearch(v)} />}
            disabled={!userFiltering}
            label={t('EventsBlock.EnableSearchLabel')}
          />
        </FormGroup>
      </OutlinedContainer>

      <OutlinedContainer fullWidth column left label={t('EventsBlock.ShowAllLinkPageTitle')}>
        {showAllLinkPage && (
          <PageListManualBlockItem
            index={0}
            onReorder={noop}
            onDelete={() => onChangeShowAllLinkPage(null)}
            overrideItemPath={`${blockPath}.showAllLinkPage`}
            color={Styles.Colors.LIGHT_GREY}
            hideHighlightCheckbox
            hideSkinSelector
            disableReordering
          />
        )}
        <Button onClick={() => setLinkPageFinderOpen(true)} variant='outlined' color='secondary'>
          {t('EventsBlock.ShowAllLinkPageButtonLabel')}
        </Button>
      </OutlinedContainer>
    </Container>
  );
};

export default PageListAutoBlock;
